import { useState } from 'react';
import { DocumentsJoints } from '../../components/DocumentsJoints.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { tr } from '../../lib/i18n';
import { C } from '../../theme/palette';

/** Ce qu'on range dans le dossier d'un client, la fiche signée d'abord. */
export const SORTES_CLIENT = [
  'Fiche d’ouverture signée',
  'Contrat',
  'Plan',
  'Courrier reçu',
  'Pièce d’identité',
  'Autre',
];

/**
 * Le classeur des comptes clients du magasin : pour chaque client, son
 * dossier — la fiche d'ouverture qu'il a renvoyée signée (photo ou PDF),
 * ses contrats, ses courriers.
 *
 * <p>« Il me renvoie l'ouverture du compte : comment je la scanne comme
 * preuve pour la mettre dans le classeur ? » La pièce se joint ici ou dans
 * la fiche du client (Compte client) ; elle se retrouve aux deux endroits.
 * Le dossier ne se lit qu'à l'ouverture : cent clients ne font pas cent
 * demandes au serveur.
 *
 * @param {object} props propriétés
 * @param {object[]} props.clients clients de la société
 * @param {object} props.styleGroupe style des titres de groupe du classeur
 * @returns {import('react').ReactElement} les comptes clients du classeur
 */
export function ClasseurClients({ clients, styleGroupe }) {
  const [ouverts, setOuverts] = useState(() => new Set());
  const liste = (clients || [])
    .filter((c) => typeof c.id === 'number')
    .sort((a, b) => String(a.nom || '').localeCompare(String(b.nom || '')));
  return (
    <>
      <div style={styleGroupe}>{tr('👥 Comptes clients — fiches signées et documents')}</div>
      {liste.length === 0 && (
        <Card>
          <div style={{ padding: 14, textAlign: 'center', color: C.muted, fontSize: 11 }}>
            {tr('Aucun client pour l’instant.')}
          </div>
        </Card>
      )}
      {liste.map((cl) => (
        <Card key={cl.id} style={{ marginBottom: 6, overflow: 'hidden' }}>
          <details data-testid="classeur-client" open={ouverts.has(cl.id)}>
            <summary
              onClick={(e) => {
                // Le dossier ne se lit qu'une fois ouvert : l'état suit le clic.
                e.preventDefault();
                setOuverts((st) => {
                  const n = new Set(st);
                  if (n.has(cl.id)) n.delete(cl.id);
                  else n.add(cl.id);
                  return n;
                });
              }}
              style={{
                padding: '12px 14px',
                cursor: 'pointer',
                listStyle: 'none',
                display: 'flex',
                gap: 11,
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 18 }}>🪪</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 800 }}>{cl.nom}</span>
                <span style={{ display: 'block', fontSize: 9.5, color: C.muted }}>
                  {tr('N° client')} {cl.id} ·{' '}
                  {cl.compteStatut === 'demande' ? tr('Demande en attente') : tr('Compte ouvert')}
                </span>
              </span>
            </summary>
            {ouverts.has(cl.id) && (
              <div style={{ padding: '0 14px 12px' }}>
                <DocumentsJoints
                  genre="CLIENT"
                  documentId={cl.id}
                  sortes={SORTES_CLIENT}
                  titre={tr('📎 DOCUMENTS DU CLIENT')}
                />
              </div>
            )}
          </details>
        </Card>
      ))}
    </>
  );
}
